import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/apiClient';
import './EvaluationDashboard.css';

const EvaluationDashboard = () => {
  const [metrics, setMetrics] = useState({});
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentMetrics, setAgentMetrics] = useState({});

  const agents = ['extractor', 'evaluator', 'summarizer', 'formatter', 'explainability'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch metrics for each agent
      const agentMetricsPromises = agents.map(async (agent) => {
        try {
          const response = await apiFetch(`/api/evals/metrics?agent_name=${agent}`);
          if (response.ok) {
            const data = await response.json();
            return { agent, metrics: data.metrics };
          }
        } catch (err) {
          console.error(`Error fetching metrics for ${agent}:`, err);
        }
        return { agent, metrics: null };
      });

      const agentMetricsResults = await Promise.all(agentMetricsPromises);
      const metricsMap = {};
      agentMetricsResults.forEach(({ agent, metrics }) => {
        metricsMap[agent] = metrics;
      });
      setAgentMetrics(metricsMap);

      // Fetch recent runs
      const runsResponse = await apiFetch('/api/evals/runs?limit=10');
      if (runsResponse.ok) {
        const runsData = await runsResponse.json();
        setRecentRuns(runsData.runs || []);
      }

      // Fetch overall metrics
      const overallResponse = await apiFetch('/api/evals/metrics');
      if (overallResponse.ok) {
        const overallData = await overallResponse.json();
        setMetrics(overallData.metrics || {});
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const handleRunEvaluation = () => {
    // Navigate to run evaluation page or open modal
    window.location.href = '/evals/run';
  };

  const handleViewResults = (runId) => {
    window.location.href = `/evals/results/${runId}`;
  };

  const formatAgentName = (agent) => {
    return agent.charAt(0).toUpperCase() + agent.slice(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'running':
        return 'status-running';
      case 'failed':
        return 'status-failed';
      default:
        return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="evaluation-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="evaluation-dashboard">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchDashboardData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="evaluation-dashboard">
      <div className="dashboard-header">
        <h1>Agent Evaluation Dashboard</h1>
        <div className="header-actions">
          <button onClick={handleRunEvaluation} className="btn-primary">
            Run Evaluation
          </button>
          <button onClick={fetchDashboardData} className="btn-secondary">
            Refresh
          </button>
        </div>
      </div>

      <div className="agent-overview">
        <h2>Agent Performance Overview</h2>
        <div className="agent-cards">
          {agents.map((agent) => {
            const agentData = agentMetrics[agent] || {};
            const accuracy = agentData.accuracy || 0;
            const totalResults = agentData.total_results || 0;
            const passedResults = agentData.passed_results || 0;
            const failedResults = agentData.failed_results || 0;

            return (
              <div key={agent} className="agent-card">
                <div className="agent-card-header">
                  <h3>{formatAgentName(agent)}</h3>
                </div>
                <div className="agent-card-body">
                  <div className="metric-row">
                    <span className="metric-label">Accuracy:</span>
                    <span className={`metric-value ${accuracy >= 0.9 ? 'high' : accuracy >= 0.8 ? 'medium' : 'low'}`}>
                      {(accuracy * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Total Results:</span>
                    <span className="metric-value">{totalResults}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Passed:</span>
                    <span className="metric-value passed">{passedResults}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Failed:</span>
                    <span className="metric-value failed">{failedResults}</span>
                  </div>
                </div>
                <div className="agent-card-footer">
                  <button
                    onClick={() => window.location.href = `/evals/results?agent_name=${agent}`}
                    className="btn-link"
                  >
                    View Details →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="recent-runs">
        <h2>Recent Evaluation Runs</h2>
        {recentRuns.length === 0 ? (
          <div className="empty-state">
            <p>No evaluation runs found. Run your first evaluation to get started.</p>
          </div>
        ) : (
          <table className="runs-table">
            <thead>
              <tr>
                <th>Run ID</th>
                <th>Eval ID</th>
                <th>Agent</th>
                <th>Status</th>
                <th>Samples</th>
                <th>Passed</th>
                <th>Failed</th>
                <th>Accuracy</th>
                <th>Started</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentRuns.map((run) => {
                const accuracy = run.total_samples > 0
                  ? (run.passed_samples / run.total_samples * 100).toFixed(1)
                  : '0.0';
                
                return (
                  <tr key={run.id}>
                    <td>{run.id}</td>
                    <td className="eval-id">{run.eval_id}</td>
                    <td>{formatAgentName(run.agent_name)}</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(run.status)}`}>
                        {run.status}
                      </span>
                    </td>
                    <td>{run.total_samples}</td>
                    <td className="passed">{run.passed_samples}</td>
                    <td className="failed">{run.failed_samples}</td>
                    <td>{accuracy}%</td>
                    <td>
                      {run.started_at
                        ? new Date(run.started_at).toLocaleString()
                        : 'N/A'}
                    </td>
                    <td>
                      <button
                        onClick={() => handleViewResults(run.id)}
                        className="btn-link"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EvaluationDashboard;



